import { useState } from 'react';
import type { AssistantCalendar, AssistantEvent, CreateEventInput, UpdateEventInput } from '@accounting/shared';
import { addDaysKey, localDateKey, pad2 } from '../../lib/calendarGrid';

/**
 * Create / edit an event. Times are entered in the browser's zone with
 * datetime-local inputs and sent as ISO instants; all-day events are entered
 * with an INCLUSIVE end date and sent exclusive, iCalendar style.
 */
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function defaultStart(anchor: Date): string {
  const d = new Date(anchor);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return toDatetimeLocal(d.toISOString());
}

export interface EventFormProps {
  calendars: AssistantCalendar[];
  /** Existing event for edit mode. */
  event?: AssistantEvent | null;
  /** Suggested start for a new event. */
  anchor?: Date;
  /** Prefill for a new event (e.g. from an email). Ignored in edit mode. */
  initial?: { subject?: string; body?: string; location?: string };
  busy?: boolean;
  error?: string | null;
  onCreate: (input: CreateEventInput) => void;
  onUpdate: (id: string, patch: UpdateEventInput) => void;
  onClose: () => void;
}

export function EventForm({ calendars, event, anchor = new Date(), initial, busy, error, onCreate, onUpdate, onClose }: EventFormProps) {
  const writable = calendars.filter((c) => c.canWrite && c.isSelected);
  const [calendarId, setCalendarId] = useState(event?.calendarId ?? writable.find((c) => c.isPrimary)?.id ?? writable[0]?.id ?? '');
  const [subject, setSubject] = useState(event?.subject ?? initial?.subject ?? '');
  const [isAllDay, setIsAllDay] = useState(event?.isAllDay ?? false);
  const [start, setStart] = useState(event && !event.isAllDay ? toDatetimeLocal(event.startsAt) : defaultStart(anchor));
  const [end, setEnd] = useState(
    event && !event.isAllDay ? toDatetimeLocal(event.endsAt) : toDatetimeLocal(new Date(new Date(`${defaultStart(anchor)}:00`).getTime() + 3_600_000).toISOString()),
  );
  const [startDate, setStartDate] = useState(event?.startDate ?? localDateKey(anchor));
  const [endDateIncl, setEndDateIncl] = useState(event?.endDate ? addDaysKey(event.endDate, -1) : localDateKey(anchor));
  const [location, setLocation] = useState(event?.location ?? initial?.location ?? '');
  const [body, setBody] = useState(event?.bodyPreview ?? initial?.body ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = () => {
    setLocalError(null);
    if (!subject.trim()) return setLocalError('Give the event a title.');
    if (!event && !calendarId) return setLocalError('Pick a calendar.');
    let s: string;
    let e: string;
    if (isAllDay) {
      s = startDate;
      e = addDaysKey(endDateIncl, 1);
      if (e <= s) return setLocalError('End date must be on or after the start date.');
    } else {
      const sd = new Date(start);
      const ed = new Date(end);
      if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return setLocalError('Enter a start and end time.');
      if (ed <= sd) return setLocalError('End must be after start.');
      s = sd.toISOString();
      e = ed.toISOString();
    }
    const common = { subject: subject.trim(), location: location.trim() || undefined, body: body.trim() || undefined, isAllDay, start: s, end: e };
    if (event) {
      onUpdate(event.id, { ...common, location: location.trim() || null, body: body.trim() || null });
    } else {
      onCreate({ calendarId, ...common });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal pa-detail" onClick={(ev) => ev.stopPropagation()} role="dialog" aria-label={event ? 'Edit event' : 'New event'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>{event ? 'Edit event' : 'New event'}</h2>
          <button type="button" className="link-button" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <form
          className="pa-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            submit();
          }}
        >
          {!event && (
            <label>
              Calendar
              <select value={calendarId} onChange={(ev) => setCalendarId(ev.target.value)}>
                {writable.length === 0 && <option value="">No writable calendars</option>}
                {writable.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.connectionLabel} · {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Title
            <input type="text" value={subject} onChange={(ev) => setSubject(ev.target.value)} autoFocus />
          </label>
          <label className="pa-form__inline">
            <input type="checkbox" checked={isAllDay} onChange={(ev) => setIsAllDay(ev.target.checked)} />
            All day
          </label>
          {isAllDay ? (
            <div className="pa-form__row">
              <label>
                Starts
                <input type="date" value={startDate} onChange={(ev) => setStartDate(ev.target.value)} />
              </label>
              <label>
                Ends (inclusive)
                <input type="date" value={endDateIncl} onChange={(ev) => setEndDateIncl(ev.target.value)} />
              </label>
            </div>
          ) : (
            <div className="pa-form__row">
              <label>
                Starts
                <input type="datetime-local" value={start} onChange={(ev) => setStart(ev.target.value)} />
              </label>
              <label>
                Ends
                <input type="datetime-local" value={end} onChange={(ev) => setEnd(ev.target.value)} />
              </label>
            </div>
          )}
          <label>
            Location
            <input type="text" value={location} onChange={(ev) => setLocation(ev.target.value)} />
          </label>
          <label>
            Notes
            <textarea rows={3} value={body} onChange={(ev) => setBody(ev.target.value)} />
          </label>
          {(localError || error) && <div className="field-error">{localError ?? error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy || (!event && writable.length === 0)}>
              {busy ? 'Saving…' : event ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
